// Admin UI for tenant-scoped property visibility (see
// docs/implementation-plans/2026-09-24-property-visibility-tenant-redesign.md).
// One settings row per tenant + object type - no named profiles.
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import client from "../api/client";
import { SectionCard } from "./ThemeColorFields";
import ButtonLoader from "./ButtonLoader";
import { withMinDuration, reloadIfCurrentTenant } from "../utils";

const OBJECT_TYPES = [
  { value: "contact", label: "Contact" },
  { value: "household", label: "Household" },
  { value: "household_map", label: "Household (Canvass Map)" },
];

const PropertyVisibilityManager = ({ tenantId, tenants = [], onTenantChange = () => {} }) => {
  const [fields, setFields] = useState({ contact: [], household: [], household_map: [] });
  const [objectType, setObjectType] = useState("contact");
  const [settings, setSettings] = useState(null);
  const [hiddenFields, setHiddenFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const loadSettings = async (isStale = () => false) => {
    if (!tenantId) {
      setFields({ contact: [], household: [], household_map: [] });
      setSettings(null);
      setHiddenFields([]);
      return;
    }
    setLoading(true);
    try {
      const [fieldsRes, settingsRes] = await withMinDuration(Promise.all([
        client.get(`/tenants/${tenantId}/property-visibility/fields`),
        client.get(`/tenants/${tenantId}/property-visibility/settings`, { params: { object_type: objectType } }),
      ]));
      if (isStale()) return;
      setFields(fieldsRes.data);
      setSettings(settingsRes.data);
      setHiddenFields(settingsRes.data.hidden_fields || []);
    } catch (error) {
      if (isStale()) return;
      toast.error(error.response?.data?.message || "Failed to load property visibility settings.");
    } finally {
      if (!isStale()) setLoading(false);
    }
  };

  // Guards against a slower response for a since-abandoned tenant/objectType
  // combo landing after a newer one and overwriting it with stale data.
  useEffect(() => {
    let cancelled = false;
    loadSettings(() => cancelled);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, objectType]);

  const toggleField = (fieldName) => {
    setHiddenFields((prev) =>
      prev.includes(fieldName) ? prev.filter((f) => f !== fieldName) : [...prev, fieldName]
    );
  };

  const checkAllFields = () => setHiddenFields([]);
  const clearAllFields = () => setHiddenFields(fieldList.map((field) => field.name));

  const handleSave = async () => {
    if (!tenantId) {
      toast.error("Select a tenant first.");
      return;
    }
    if (!settings) return;
    setIsSaving(true);
    try {
      const [res] = await Promise.all([
        client.put(`/tenants/${tenantId}/property-visibility/settings/${settings.id}`, {
          hidden_fields: hiddenFields,
        }),
        new Promise((resolve) => setTimeout(resolve, 1000)),
      ]);
      setSettings(res.data);
      setHiddenFields(res.data.hidden_fields || []);
      toast.success("Property visibility saved.");
      reloadIfCurrentTenant(tenantId);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save property visibility.");
    } finally {
      setIsSaving(false);
    }
  };

  const fieldList = fields[objectType] || [];

  return (
    <SectionCard
      title="Property Visibility"
      description="Choose which Contact or Household fields are hidden from this tenant's canvassers."
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex flex-col gap-2 w-[320px]">
            <h6 className="text-canvas-directoryText">Tenant</h6>
            <select
              className="border border-canvas-tersery rounded p-3 text-sm font-outfit h-[42px]"
              value={tenantId || ""}
              onChange={(e) => onTenantChange(e.target.value ? Number(e.target.value) : null)}
            >
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-2 w-[220px]">
            <h6 className="text-canvas-directoryText">Object Type</h6>
            <select
              className="border border-canvas-tersery rounded p-3 text-sm font-outfit h-[42px]"
              value={objectType}
              onChange={(e) => setObjectType(e.target.value)}
              disabled={!tenantId}
            >
              {OBJECT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
        </div>

        {!tenantId ? null : loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 border border-canvas-tersery rounded p-4">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="h-4 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={checkAllFields}
                className="cursor-pointer text-canvas-primary underline font-outfit text-xs font-bold uppercase"
              >
                Select All
              </button>
              <button
                type="button"
                onClick={clearAllFields}
                className="cursor-pointer text-canvas-danger underline font-outfit text-xs font-bold uppercase"
              >
                Clear All
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 border border-canvas-tersery rounded p-4">
              {fieldList.map((field) => (
                <label key={field.name} className="flex items-center gap-2 text-sm font-outfit text-canvas-directoryText cursor-pointer">
                  <input
                    type="checkbox"
                    aria-label={`Show ${field.label}`}
                    className="w-4 h-4 shrink-0 accent-canvas-primary cursor-pointer focus:outline-none focus:ring-2 focus:ring-canvas-primary/50 focus:ring-offset-1"
                    checked={!hiddenFields.includes(field.name)}
                    onChange={() => toggleField(field.name)}
                  />
                  {field.label}
                </label>
              ))}
            </div>
            <div>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="cursor-pointer bg-canvas-primary text-canvas-white rounded px-6 py-3 uppercase font-outfit text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSaving && <ButtonLoader />}
                {isSaving ? "Saving..." : "Save Visibility Rules"}
              </button>
            </div>
          </div>
        )}
      </div>
    </SectionCard>
  );
};

export default PropertyVisibilityManager;
